import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/themeCheckbox.css";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  ADMIN_THEME,
  adminEditLabelStyle,
  adminEditInputStyle,
  parseSiteRegisterDate,
  formatSiteRegisterDate,
  siteRegisterDatePickerProps,
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
} from "./adminEditModalTheme";

function AdditionalConcernsPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [uploadingKey, setUploadingKey] = useState(null);
  const generatingPdfRef = useRef(false);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [showViewModal, setShowViewModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [newRecord, setNewRecord] = useState({
    formUuid: "",
    userId: "",
    siteLocation: "",
    engineer: "",
    date: "",
    estimatedTime: "",
    jobReference: "",
    safetyIssues: "",
    proximity: "",
    specialistEquipment: "",
    cableRouteEg: "",
    otherComments: "",
    mcqQuestions: []
  });

  // Parse MCQ questions from string format
  const parseMcqQuestions = (mcqString) => {
    try {
      if (!mcqString) return [];
      
      // If it's already an array, return it
      if (Array.isArray(mcqString)) return mcqString;
      
      // If it's a string, try to parse it as JSON first
      if (typeof mcqString === 'string') {
        try {
          // Try direct JSON parsing first (for proper JSON strings)
          const parsed = JSON.parse(mcqString);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          // If JSON parsing fails, try manual parsing for the old format
          const cleanStr = mcqString.replace(/^\[|\]$/g, '');
          const items = [];
          let currentItem = '';
          let braceCount = 0;
          let inItem = false;
          
          for (let i = 0; i < cleanStr.length; i++) {
            const char = cleanStr[i];
            if (char === '{' && !inItem) {
              inItem = true;
              braceCount = 1;
              currentItem = char;
            } else if (inItem) {
              currentItem += char;
              if (char === '{') {
                braceCount++;
              } else if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                  // Parse the item
                  const item = parseMcqItem(currentItem);
                  if (item) items.push(item);
                  currentItem = '';
                  inItem = false;
                }
              }
            }
          }
          return items;
        }
      }
      return [];
    } catch (error) {
      console.error('Error parsing MCQ questions:', error);
      return [];
    }
  };

  // Parse individual MCQ item
  const parseMcqItem = (itemStr) => {
    try {
      // Extract text
      const textMatch = itemStr.match(/'text':\s*'([^']*)'/);
      const text = textMatch ? textMatch[1] : '';
      
      // Extract options
      const optionsMatch = itemStr.match(/'options':\s*\[([^\]]*)\]/);
      const options = optionsMatch ? 
        optionsMatch[1].split(',').map(opt => opt.trim().replace(/['"]/g, '')) : [];
      
      // Extract value
      const valueMatch = itemStr.match(/'value':\s*'([^']*)'/);
      const value = valueMatch ? valueMatch[1] : '';
      
      // Extract textField
      const textFieldMatch = itemStr.match(/'textField':\s*\{([^}]*)\}/);
      const textField = {};
      if (textFieldMatch) {
        const tfStr = textFieldMatch[1];
        const isTextBoxMatch = tfStr.match(/'isTextBox':\s*(true|false)/);
        textField.isTextBox = isTextBoxMatch ? isTextBoxMatch[1] === 'true' : false;
        
        const hintTextMatch = tfStr.match(/'hintText':\s*'([^']*)'/);
        textField.hintText = hintTextMatch ? hintTextMatch[1] : '';
        
        const valueTypeMatch = tfStr.match(/'valueType':\s*'([^']*)'/);
        textField.valueType = valueTypeMatch ? valueTypeMatch[1] : '';
      }
      
      // Extract img
      const imgMatch = itemStr.match(/'img':\s*\{([^}]*)\}/);
      const img = {};
      if (imgMatch) {
        const imgStr = imgMatch[1];
        const isImageMatch = imgStr.match(/'isImage':\s*(true|false)/);
        img.isImage = isImageMatch ? isImageMatch[1] === 'true' : false;
        
        const imageUrlMatch = imgStr.match(/'imageUrl':\s*'([^']*)'/);
        img.imageUrl = imageUrlMatch ? imageUrlMatch[1] : '';
      }
      
      // Extract isOptional
      const isOptionalMatch = itemStr.match(/'isOptional':\s*(true|false)/);
      const isOptional = isOptionalMatch ? isOptionalMatch[1] === 'true' : false;
      
      return {
        text,
        options,
        value,
        textField,
        img,
        isOptional
      };
    } catch (error) {
      console.error('Error parsing MCQ item:', error);
      return null;
    }
  };

  // Fetch records on component mount
  useEffect(() => {
    async function fetchRecords() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/additional-concerns", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setRecords(data.data);
        } else {
          setRecords([]);
        }
      } catch {
        setRecords([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchRecords();
    fetchAppUsers();
  }, []);

  // Search logic
  const filtered = records.filter(r => {
    const fields = [r.siteLocation, r.engineer, r.jobReference, r.date].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // Handle text input changes
  const handleTextChange = (field, value) => {
    setNewRecord(prev => ({ ...prev, [field]: value }));
  };

  const handleEditTextChange = (field, value) => {
    setEditRecord(prev => ({ ...prev, [field]: value }));
  };

  // Handle radio button changes

  // MCQ change handlers
  const handleMcqChange = (itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedMcq = [...editRecord.mcqQuestions];
      updatedMcq[itemIdx] = { ...updatedMcq[itemIdx], value };
      // If changing to "No", clear the image
      if (value === "No" && updatedMcq[itemIdx].img?.isImage) {
        updatedMcq[itemIdx].img = { ...updatedMcq[itemIdx].img, imageUrl: '' };
      }
      setEditRecord({ ...editRecord, mcqQuestions: updatedMcq });
    } else {
      const updatedMcq = [...newRecord.mcqQuestions];
      updatedMcq[itemIdx] = { ...updatedMcq[itemIdx], value };
      // If changing to "No", clear the image
      if (value === "No" && updatedMcq[itemIdx].img?.isImage) {
        updatedMcq[itemIdx].img = { ...updatedMcq[itemIdx].img, imageUrl: '' };
      }
      setNewRecord({ ...newRecord, mcqQuestions: updatedMcq });
    }
  };

  const handleMcqTextFieldChange = (itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedMcq = [...editRecord.mcqQuestions];
      updatedMcq[itemIdx] = { 
        ...updatedMcq[itemIdx], 
        textField: { ...updatedMcq[itemIdx].textField, valueType: value }
      };
      setEditRecord({ ...editRecord, mcqQuestions: updatedMcq });
    } else {
      const updatedMcq = [...newRecord.mcqQuestions];
      updatedMcq[itemIdx] = { 
        ...updatedMcq[itemIdx], 
        textField: { ...updatedMcq[itemIdx].textField, valueType: value }
      };
      setNewRecord({ ...newRecord, mcqQuestions: updatedMcq });
    }
  };

  // Handle image upload for MCQ questions
  const handleImageUpload = (itemIdx, file, isEdit = false) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      if (isEdit) {
        const updatedMcq = [...editRecord.mcqQuestions];
        if (!updatedMcq[itemIdx].img) {
          updatedMcq[itemIdx].img = { isImage: true, imageUrl: '' };
        }
        updatedMcq[itemIdx].img = { 
          ...updatedMcq[itemIdx].img, 
          isImage: true, 
          imageUrl: base64String 
        };
        setEditRecord({ ...editRecord, mcqQuestions: updatedMcq });
      } else {
        const updatedMcq = [...newRecord.mcqQuestions];
        if (!updatedMcq[itemIdx].img) {
          updatedMcq[itemIdx].img = { isImage: true, imageUrl: '' };
        }
        updatedMcq[itemIdx].img = { 
          ...updatedMcq[itemIdx].img, 
          isImage: true, 
          imageUrl: base64String 
        };
        setNewRecord({ ...newRecord, mcqQuestions: updatedMcq });
      }
    };
    reader.onerror = () => {
      toast.error("Failed to read image file");
    };
    reader.readAsDataURL(file);
  };

  // Add Record
  const handleAdd = async () => {
    if (!newRecord.siteLocation || !newRecord.engineer || !newRecord.date || !newRecord.jobReference) {
      toast.error("All required fields must be filled");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Convert MCQ questions to string format
      const mcqString = JSON.stringify(newRecord.mcqQuestions);
      
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === newRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const recordToSave = { 
        ...newRecord, 
        formUuid: ensureFormUuid(newRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        mcqQuestions: mcqString 
      };
      
      const res = await fetch(baseUrl + "/api/additional-concerns", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(recordToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        setRecords([...records, data.data]);
        setShowAddModal(false);
        setNewRecord({
          formUuid: "",
          userId: "",
          siteLocation: "",
          engineer: "",
          date: "",
          estimatedTime: "",
          jobReference: "",
          safetyIssues: "",
          proximity: "",
          specialistEquipment: "",
          cableRouteEg: "",
          otherComments: "",
          outOfHours: "",
          needCOSS: "",
          otherContractors: "",
          contractorDetails: "",
          photosTaken: "",
          photoComments: "",
          mcqQuestions: []
        });
        toast.success("Record added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Edit Record
  const handleEdit = (record) => {
    const parsedMcq = parseMcqQuestions(record.mcqQuestions);
    setEditRecord({ ...record, formUuid: ensureFormUuid(record?.formUuid), mcqQuestions: parsedMcq });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editRecord.siteLocation || !editRecord.engineer || !editRecord.date || !editRecord.jobReference) {
      toast.error("All required fields must be filled");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Convert MCQ questions back to string format
      const mcqString = JSON.stringify(editRecord.mcqQuestions);
      
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === editRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const recordToSave = { 
        ...editRecord, 
        formUuid: ensureFormUuid(editRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        mcqQuestions: mcqString 
      };
      
      const res = await fetch(baseUrl + "/api/additional-concerns/" + editRecord._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(recordToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.map(r => r._id === editRecord._id ? data.data : r));
        setShowEditModal(false);
        setEditRecord(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  // Delete Record
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      const res = await fetch(baseUrl + "/api/additional-concerns/" + deleteId, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter(r => r._id !== deleteId));
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  // View Record
  const handleView = (record) => {
    const parsedMcq = parseMcqQuestions(record.mcqQuestions);
    setViewRecord({ ...record, mcqQuestions: parsedMcq });
    setShowViewModal(true);
  };

  // Fetch MCQ template when opening Add Modal
  const fetchMcqTemplate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/additional-concerns/template", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setNewRecord(prev => ({ ...prev, mcqQuestions: data.data }));
      }
    } catch (error) {
      console.error('Failed to fetch MCQ template:', error);
    }
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.siteLocation || '').trim();

    if (!locationForUpload) {
      toast.error('Site location is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  // Generate PDF
  const generatePDF = async (record, options = {}) => {
    if (generatingPdfRef.current) return; // Prevent duplicate calls
    generatingPdfRef.current = true;
    
    try {
      const shouldUploadToSharePoint = options.uploadOnly === true;
      const uploadConfig = shouldUploadToSharePoint ? {
        phaseType: options.phaseType || 'non-phase',
        phaseNumber: options.phaseNumber || '',
        network: options.network || '',
        siteName: options.siteName || '',
        onSuccess: options.onSuccess,
        onError: options.onError
      } : null;
      
      const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Modern color scheme
    const primaryColor = [139, 22, 52]; // #8b1634
    const secondaryColor = [233, 207, 214]; // #e9cfd6
    const headerColor = [139, 22, 52]; // Dark magenta
    
    // Helper function to draw modern rounded rectangle
    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
      if (hasShadow) {
        doc.setFillColor(0, 0, 0, 0.1);
        doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
      }
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, 'FD');
    };
    
    // ---- HEADER SECTION ----
    const headerHeight = 30;
    drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
    
    // ---- ADD HIGH-RES LOGO ----
    const addLogoToPDF = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = function () {
          try {
            const logoWidth = 70;
            const aspectRatio = logoImg.height / logoImg.width;
            const logoHeight = logoWidth * aspectRatio;
            const logoX = 10;
            const logoY = (headerHeight - logoHeight) / 2;
            
            const canvas = document.createElement('canvas');
            const scale = 2.5;
            canvas.width = logoImg.width * scale;
            canvas.height = logoImg.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.scale(scale, scale);
            ctx.drawImage(logoImg, 0, 0);
            
            const highResDataUrl = canvas.toDataURL('image/png', 1.0);
            doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
          } catch (error) {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
          }
          resolve();
        };
        logoImg.onerror = function () {
          doc.setFillColor(255, 255, 255);
          doc.rect(10, 6, 60, 15, 'F');
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text("TAYLOR TECHNOLOGY", 15, 12);
          doc.text("SYSTEMS LIMITED", 15, 17);
          resolve();
        };
        logoImg.src = onsiteproLogo;
      });
    };
    
    await addLogoToPDF();
    
    // ---- HEADER TEXT ----
    const titleX = 90;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("Additional Concerns Assessment", titleX, 13);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
    
    let yPosition = headerHeight + 10;
    
    // ---- Section 1: Basic Information ----
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("1", 14, yPosition + 5.5);
    doc.text("Basic Information", 22, yPosition + 5.5);
    yPosition += 8;
    
    autoTable(doc, {
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 70 },
        2: { cellWidth: 30 },
        3: { cellWidth: 40 },
      },
      head: [
        [
          { content: "Site / Location:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.siteLocation || "", styles: { halign: "center" } },
          { content: "Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.date || "", styles: { halign: "center" } },
        ],
      ],
      body: [
        [
          { content: "Engineer:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.engineer || "", styles: { halign: "center" } },
          { content: "Job Reference:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.jobReference || "", styles: { halign: "center" } },
        ],
        [
          { content: "Estimated Time:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.estimatedTime || "", styles: { halign: "center" } },
          { content: "", styles: { fillColor: [230, 230, 230] } },
          { content: "", styles: {} },
        ],
      ],
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
        textColor: [0, 0, 0],
      },
    });
    
    yPosition = doc.lastAutoTable.finalY + 2;
    
    // ---- Section 2: Details ----
    if (yPosition + 60 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("2", 14, yPosition + 5.5);
    doc.text("Details", 22, yPosition + 5.5);
    yPosition += 8;
    
    const detailsData = [
      ["Safety Issues:", record.safetyIssues || "N/A"],
      ["Proximity to on or near the line:", record.proximity || "N/A"],
      ["Specialist Equipment Required:", record.specialistEquipment || "N/A"],
      ["Type of Cable Route:", record.cableRouteEg || "N/A"],
      ["Other Comments:", record.otherComments || "N/A"],
    ];
    
    autoTable(doc, {
      startY: yPosition,
      head: [[
        { content: "Field", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
        { content: "Value", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
      ]],
      body: detailsData.map(row => [
        { content: row[0], styles: { fillColor: [245, 245, 245], fontStyle: "bold" } },
        { content: row[1], styles: { halign: "left" } },
      ]),
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 4,
        halign: "left",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        textColor: [0, 0, 0],
        fillColor: [245, 245, 245],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
      },
    });
    
    yPosition = doc.lastAutoTable.finalY + 2;
    
    // ---- Section 3: MCQ Questions ----
    let mcqQuestions = [];
    try {
      mcqQuestions = parseMcqQuestions(record.mcqQuestions);
    } catch (err) {
      mcqQuestions = [];
    }
    
    if (mcqQuestions && mcqQuestions.length > 0) {
      // Check if we need a new page
      if (yPosition + 50 > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Section Header
      doc.setFillColor(139, 22, 52);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.rect(10, yPosition, pageWidth - 20, 8, "F");
      doc.text("3", 14, yPosition + 5.5);
      doc.text("MCQ Questions", 22, yPosition + 5.5);
      yPosition += 8;
      
      // Pre-process images for PDF
      const processedImages = new Map();
      const imagePromises = mcqQuestions.map((item, idx) => {
        if (item && item.img?.isImage && item.img?.imageUrl) {
          return new Promise((resolve) => {
            try {
              let imgSrc = item.img.imageUrl;
              if (!imgSrc.startsWith('data:image')) {
                imgSrc = 'data:image/png;base64,' + imgSrc;
              }
              
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                try {
                  // Larger size for better visibility (max 35x25 mm)
                  // Cell width is 40mm, with padding we have ~38mm usable space
                  const maxWidth = 35;
                  const maxHeight = 25;
                  const aspectRatio = img.width / img.height;
                  let finalWidth = maxWidth;
                  let finalHeight = maxWidth / aspectRatio;
                  
                  if (finalHeight > maxHeight) {
                    finalHeight = maxHeight;
                    finalWidth = maxHeight * aspectRatio;
                  }
                  
                  // Ensure width doesn't exceed maxWidth
                  if (finalWidth > maxWidth) {
                    finalWidth = maxWidth;
                    finalHeight = maxWidth / aspectRatio;
                  }
                  
                  // Much higher scale for better quality (6x for very sharp rendering)
                  const scale = 6;
                  const canvas = document.createElement('canvas');
                  canvas.width = Math.round(finalWidth * scale);
                  canvas.height = Math.round(finalHeight * scale);
                  const ctx = canvas.getContext('2d');
                  
                  // Best image rendering settings for sharpness
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = 'high';
                  ctx.fillStyle = '#FFFFFF';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  
                  // Use nearest-neighbor for upscaling then smooth for better quality
                  // Draw image at high resolution for maximum sharpness
                  ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
                  
                  // Use PNG with maximum quality
                  const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                  
                  processedImages.set(idx, {
                    dataUrl: highResDataUrl,
                    width: finalWidth,
                    height: finalHeight
                  });
                } catch (err) {
                  console.error('Error processing image for PDF:', err);
                }
                resolve();
              };
              img.onerror = () => {
                console.error('Error loading image for PDF');
                resolve();
              };
              img.src = imgSrc;
            } catch (err) {
              console.error('Error processing image for PDF:', err);
              resolve();
            }
          });
        }
        return Promise.resolve();
      });
      
      // Wait for all images to load
      await Promise.all(imagePromises);
      
      // MCQ Questions Table
      const mcqTableBody = mcqQuestions.map((item, idx) => {
        const answerText = item.value || 'Not answered';
        const contactVal = item.textField?.isTextBox && item.textField?.valueType
          ? item.textField.valueType
          : '';

        return [
          { content: `Q${idx + 1}: ${item.text || ''}`, styles: { halign: "left", fontStyle: "bold" } },
          { content: answerText, styles: { halign: "left" } },
          { content: contactVal, styles: { halign: "left" } },
          { content: '', styles: { halign: "center" } } // Empty for image
        ];
      });
      
      autoTable(doc, {
        startY: yPosition,
        columnStyles: {
          0: { cellWidth: 70 }, // Question
          1: { cellWidth: 30 }, // Answer
          2: { cellWidth: 40 }, // Contact Name & Number
          3: { cellWidth: 50 }, // Image
        },
        head: [[
          { content: "Question", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Answer", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Contact Name & Number", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Image", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
        ]],
        body: mcqTableBody,
        didDrawCell: (data) => {
          if (data.section === 'body') {
            const item = mcqQuestions[data.row.index];

            // Column 3: Image
            if (data.column.index === 3) {
              const imageData = processedImages.get(data.row.index);
              if (imageData) {
                try {
                  const cell = data.cell;
                  const cellPadding = 1;
                  const maxAvailableWidth = cell.width - (cellPadding * 2);
                  const maxAvailableHeight = cell.height - (cellPadding * 2);
                  let finalWidth = imageData.width;
                  let finalHeight = imageData.height;
                  if (finalWidth > maxAvailableWidth) {
                    const scale = maxAvailableWidth / finalWidth;
                    finalWidth = maxAvailableWidth;
                    finalHeight = finalHeight * scale;
                  }
                  if (finalHeight > maxAvailableHeight) {
                    const scale = maxAvailableHeight / finalHeight;
                    finalHeight = maxAvailableHeight;
                    finalWidth = finalWidth * scale;
                  }
                  const imgY = cell.y + (cell.height - finalHeight) / 2;
                  const adjustedX = cell.x + (cell.width - finalWidth) / 2;
                  doc.addImage(imageData.dataUrl, 'PNG', adjustedX, imgY, finalWidth, finalHeight, '', 'NONE');
                } catch (err) {
                  console.error('Error adding image to PDF:', err);
                }
              }
            }
          }
        },
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 4,
          halign: "left",
          valign: "middle",
        },
        theme: "grid",
        tableWidth: pageWidth - 20,
        margin: { left: 10 },
        headStyles: {
          textColor: [0, 0, 0],
          fillColor: [245, 245, 245],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineWidth: 0.2,
        },
      });
      
      yPosition = doc.lastAutoTable.finalY + 2;
    }
    
    // ---- Footer Section ----
    const footerY = pageHeight - 25;
    drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      20,
      footerY + 8
    );
    doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
    
    // Decorative border removed
    
    // Build filename and either save or upload
    const safeDate = String(record.date || 'Assessment').replace(/[\\/:"*?<>|]+/g, '-');
    
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        addedByName(record),
        record.siteLocation,
        toDdMmYyyy(record.date),
      ]);
    } else {
      const safeName = String(record.siteLocation || 'AdditionalConcerns').replace(/[\\/:"*?<>|]+/g, '-');
      fileName = `AdditionalConcerns_${safeName}_${safeDate}.pdf`;
    }
    
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ''}|${record.siteLocation || ''}|${record.date || ''}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const dataUri = doc.output('datauristring');
        
        // Prepare SharePoint upload parameters
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "reactive-southeastern-tablets", uploadSubFolder: "LSER Additional Concerns Sheet"
        };
        
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: 'POST',
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = 'SharePoint upload failed';
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch (_) {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) {
          uploadConfig.onSuccess();
        }
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) {
          uploadConfig.onError(err);
        }
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("Professional Additional Concerns PDF generated successfully!");
    }
    } finally {
      generatingPdfRef.current = false;
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Additional Concerns</h2>
          
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 0.9fr 1fr 1fr 1fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Site Location</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Engineer</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Job Reference</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 0.9fr 1fr 1fr 1fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.siteLocation || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.engineer || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.date || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.jobReference || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt || r.date)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.siteLocation || ''}|${r.date || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleDelete(r._id)}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add Additional Concerns</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-4">
                    <label>Site / Location *</label>
                    <input type="text" className="form-control" value={newRecord.siteLocation} onChange={e=>handleTextChange("siteLocation", e.target.value)} required />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Engineer *</label>
                    <input type="text" className="form-control" value={newRecord.engineer} onChange={e=>handleTextChange("engineer", e.target.value)} required />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Date *</label>
                    <input type="date" className="form-control" value={newRecord.date ? newRecord.date.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("date", e.target.value.split('-').reverse().join('/'))} required />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Estimated Time</label>
                    <input type="text" className="form-control" placeholder="Estimated Time" value={newRecord.estimatedTime} onChange={e=>handleTextChange("estimatedTime", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Job Reference *</label>
                    <input type="text" className="form-control" value={newRecord.jobReference} onChange={e=>handleTextChange("jobReference", e.target.value)} required />
                  </div>
                  <div className="mb-2 col-12">
                    <label>Any Safety issues eg â€“ Working at height</label>
                    <textarea className="form-control" value={newRecord.safetyIssues} onChange={e=>handleTextChange("safetyIssues", e.target.value)} rows={2} />
                  </div>
                  <div className="mb-2 col-12">
                    <label>Proximity to on or near the line</label>
                    <textarea className="form-control" value={newRecord.proximity} onChange={e=>handleTextChange("proximity", e.target.value)} rows={2} />
                  </div>
                  <div className="mb-2 col-12">
                    <label>Specialist Equipment Required egâ€¦</label>
                    <textarea className="form-control" value={newRecord.specialistEquipment} onChange={e=>handleTextChange("specialistEquipment", e.target.value)} rows={2} />
                  </div>
                  <div className="mb-2 col-12">
                    <label>Type of Cable Route EG. Duct/Concrete/Direct</label>
                    <textarea className="form-control" value={newRecord.cableRouteEg} onChange={e=>handleTextChange("cableRouteEg", e.target.value)} rows={2} />
                  </div>
                  <div className="mb-2 col-12">
                    <label>Any Other Comments</label>
                    <textarea className="form-control" value={newRecord.otherComments} onChange={e=>handleTextChange("otherComments", e.target.value)} rows={2} />
                  </div>
                  {/* MCQ Questions Section - Dynamic from API */}
                  <div className="mb-2 col-12">
                    <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions</label>
                    {Array.isArray(newRecord.mcqQuestions) && newRecord.mcqQuestions.map((item, idx) => (
                      <div key={idx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        <div className="fw-medium" style={{marginBottom:8}}>{item.text}</div>
                        <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center'}}>
                          {Array.isArray(item.options) && item.options.map(opt => (
                            <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                              <input type="radio" name={`add-mcq-${idx}`} checked={item.value === opt} onChange={()=>handleMcqChange(idx, opt, false)} /> {opt}
                            </label>
                          ))}
                          {item.textField?.isTextBox && (
                            <div style={{display:'flex', alignItems:'center', gap:12}}>
                              <input 
                                type="text" 
                                className="form-control" 
                                style={{
                                  maxWidth: 320,
                                  fontSize: '16px',
                                  fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                  lineHeight: '1.5',
                                  padding: '8px 12px'
                                }}
                                placeholder={item.textField?.hintText || ''} 
                                value={item.textField?.valueType || ''}
                                onChange={e=>handleMcqTextFieldChange(idx, e.target.value, false)} 
                              />
                            </div>
                          )}
                        </div>
                        {/* Image Upload Section - Only show when answer is "Yes" */}
                        {item.img?.isImage && item.value === "Yes" && (
                          <div style={{marginTop:12, display:'flex', flexDirection:'column', gap:8}}>
                            <label style={{fontSize:'14px', fontWeight:'500'}}>Photo:</label>
                            {item.img?.imageUrl && (
                              <div style={{marginBottom:8}}>
                                <img 
                                  src={item.img.imageUrl.startsWith('data:image') ? item.img.imageUrl : `data:image/png;base64,${item.img.imageUrl}`}
                                  alt="Uploaded" 
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '4px',
                                    backgroundColor: 'white'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            <input 
                              type="file" 
                              accept="image/*" 
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleImageUpload(idx, file, false);
                                }
                              }}
                              className="form-control"
                              style={{maxWidth: 400}}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editRecord && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>Edit Additional Concerns</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Site / Location *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord.siteLocation} onChange={e=>handleEditTextChange("siteLocation", e.target.value)} required />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Engineer *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord.engineer} onChange={e=>handleEditTextChange("engineer", e.target.value)} required />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Date *</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord.date)}
                        onChange={(d) => handleEditTextChange("date", formatSiteRegisterDate(d))}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Estimated Time</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} placeholder="Estimated Time" value={editRecord.estimatedTime} onChange={e=>handleEditTextChange("estimatedTime", e.target.value)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Job Reference *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord.jobReference} onChange={e=>handleEditTextChange("jobReference", e.target.value)} required />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Safety issues</label>
                    <textarea className="form-control" style={adminEditInputStyle} value={editRecord.safetyIssues} onChange={e=>handleEditTextChange("safetyIssues", e.target.value)} rows={2} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Proximity</label>
                    <textarea className="form-control" style={adminEditInputStyle} value={editRecord.proximity} onChange={e=>handleEditTextChange("proximity", e.target.value)} rows={2} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Specialist Equipment</label>
                    <textarea className="form-control" style={adminEditInputStyle} value={editRecord.specialistEquipment} onChange={e=>handleEditTextChange("specialistEquipment", e.target.value)} rows={2} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Cable Route</label>
                    <textarea className="form-control" style={adminEditInputStyle} value={editRecord.cableRouteEg} onChange={e=>handleEditTextChange("cableRouteEg", e.target.value)} rows={2} />
                  </div>
                  <div className="col-12">
                    <label style={adminEditLabelStyle}>Any Other Comments</label>
                    <textarea className="form-control" style={adminEditInputStyle} value={editRecord.otherComments} onChange={e=>handleEditTextChange("otherComments", e.target.value)} rows={2} />
                  </div>
                  {/* MCQ Questions Section - Dynamic from API */}
                  <div className="col-12">
                    <label style={{ ...adminEditLabelStyle, color: ADMIN_THEME, fontSize: 14, marginBottom: 10 }}>MCQ Questions</label>
                    {Array.isArray(editRecord.mcqQuestions) && editRecord.mcqQuestions.map((item, idx) => (
                      <div key={idx} style={{ border: "1px solid #e8e8e8", borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
                        <div className="fw-medium" style={{ padding: "10px 14px", background: "#f8f4f5", borderBottom: "1px solid #e8e8e8", color: ADMIN_THEME, fontWeight: 600 }}>{item.text}</div>
                        <div style={{ padding: "12px 14px", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                          {Array.isArray(item.options) && item.options.map(opt => (
                            <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                              <input type="radio" className="form-check-input theme-checkbox" name={`edit-mcq-${idx}`} checked={item.value === opt} onChange={()=>handleMcqChange(idx, opt, true)} /> {opt}
                            </label>
                          ))}
                          {item.textField?.isTextBox && (
                            <div style={{display:'flex', alignItems:'center', gap:12}}>
                              <input
                                type="text"
                                className="form-control"
                                style={{
                                  maxWidth: 320,
                                  fontSize: '16px',
                                  fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                  lineHeight: '1.5',
                                  padding: '8px 12px'
                                }}
                                placeholder={item.textField?.hintText || ''}
                                value={item.textField?.valueType || ''}
                                onChange={e=>handleMcqTextFieldChange(idx, e.target.value, true)}
                              />
                            </div>
                          )}
                        </div>
                        {/* Image Upload Section - Only show when answer is "Yes" */}
                        {item.img?.isImage && item.value === "Yes" && (
                          <div style={{marginTop:12, display:'flex', flexDirection:'column', gap:8}}>
                            <label style={{fontSize:'14px', fontWeight:'500'}}>Photo:</label>
                            {item.img?.imageUrl && (
                              <div style={{marginBottom:8}}>
                                <img
                                  src={item.img.imageUrl.startsWith('data:image') ? item.img.imageUrl : `data:image/png;base64,${item.img.imageUrl}`}
                                  alt="Uploaded"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '4px',
                                    backgroundColor: 'white'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleImageUpload(idx, file, true);
                                }
                              }}
                              className="form-control"
                              style={{maxWidth: 400}}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleUpdate}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Additional Concerns File"
        importPath="additional-concerns/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete this record? This action cannot be undone.
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">View Additional Concerns</h5>
                <button type="button" className="btn-close" onClick={()=>setShowViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-md-6">
                    <label><strong>User ID:</strong></label>
                    <p>{viewRecord.userId || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-md-6">
                    <label><strong>Site / Location:</strong></label>
                    <p>{viewRecord.siteLocation || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-md-6">
                    <label><strong>Engineer:</strong></label>
                    <p>{viewRecord.engineer || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-md-6">
                    <label><strong>Date:</strong></label>
                    <p>{viewRecord.date || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-md-6">
                    <label><strong>Estimated Time:</strong></label>
                    <p>{viewRecord.estimatedTime || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-md-6">
                    <label><strong>Job Reference:</strong></label>
                    <p>{viewRecord.jobReference || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-12">
                    <label><strong>Any Safety issues eg â€“ Working at height:</strong></label>
                    <p>{viewRecord.safetyIssues || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-12">
                    <label><strong>Proximity to on or near the line:</strong></label>
                    <p>{viewRecord.proximity || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-12">
                    <label><strong>Specialist Equipment Required egâ€¦:</strong></label>
                    <p>{viewRecord.specialistEquipment || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-12">
                    <label><strong>Type of Cable Route EG. Duct/Concrete/Direct:</strong></label>
                    <p>{viewRecord.cableRouteEg || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-12">
                    <label><strong>Any Other Comments:</strong></label>
                    <p>{viewRecord.otherComments || 'N/A'}</p>
                  </div>
                  {/* MCQ Questions Section - Dynamic from API */}
                  <div className="mb-2 col-12">
                    <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions</label>
                    {Array.isArray(viewRecord.mcqQuestions) && viewRecord.mcqQuestions.map((item, idx) => (
                      <div key={idx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        <div className="fw-medium" style={{marginBottom:8}}>{item.text}</div>
                        <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center'}}>
                          {Array.isArray(item.options) && item.options.length > 0 && (
                            <div style={{display:'flex', alignItems:'center', gap:6}}>
                              <strong>Answer:</strong>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: item.value === 'Yes' ? '#d4edda' : item.value === 'No' ? '#f8d7da' : '#e2e3e5',
                                color: item.value === 'Yes' ? '#155724' : item.value === 'No' ? '#721c24' : '#383d41',
                                fontWeight: '500'
                              }}>
                                {item.value || 'Not answered'}
                              </span>
                            </div>
                          )}
                          {item.textField?.isTextBox && item.textField?.valueType && (
                            <div style={{display:'flex', alignItems:'center', gap:6}}>
                              <strong>Text Input:</strong>
                              <span style={{padding: '4px 8px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6'}}>
                                {item.textField.valueType}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Image Display Section - Only show when answer is "Yes" */}
                        {item.img?.isImage && item.value === "Yes" && item.img?.imageUrl && (
                          <div style={{marginTop:12, display:'flex', flexDirection:'column', gap:8}}>
                            <label style={{fontSize:'14px', fontWeight:'500'}}>Photo:</label>
                            <div>
                              <img 
                                src={item.img.imageUrl.startsWith('data:image') ? item.img.imageUrl : `data:image/png;base64,${item.img.imageUrl}`}
                                alt="Uploaded" 
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '300px',
                                  objectFit: 'contain',
                                  border: '1px solid #ddd',
                                  borderRadius: '8px',
                                  padding: '4px',
                                  backgroundColor: 'white'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowViewModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default AdditionalConcernsPage;
